import { useState } from "react";
import {
  useSendMailMessage,
  type MailAccount,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { X } from "lucide-react";

export interface ComposerInitial {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyText?: string;
  inReplyTo?: string | null;
  references?: string | null;
}

export default function MailComposer({
  account,
  initial,
  onClose,
  onSent,
}: {
  account: MailAccount;
  initial?: ComposerInitial | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const { toast } = useToast();
  const [to, setTo] = useState((initial?.to ?? []).join(", "));
  const [cc, setCc] = useState((initial?.cc ?? []).join(", "));
  const [showCc, setShowCc] = useState(Boolean(initial?.cc?.length));
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [body, setBody] = useState(
    initial?.bodyText ??
      (account.signature ? `\n\n--\n${account.signature}` : ""),
  );

  const send = useSendMailMessage({
    mutation: {
      onSuccess: () => onSent(),
      onError: (err: Error) =>
        toast({
          title: "Envoi échoué",
          description: err.message,
          variant: "destructive",
        }),
    },
  });

  const parseAddresses = (s: string) =>
    s
      .split(/[,;\n]/)
      .map((p) => p.trim())
      .filter((p) => p.includes("@"));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const toList = parseAddresses(to);
    if (toList.length === 0) {
      toast({ title: "Ajoute au moins un destinataire", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Le message est vide", variant: "destructive" });
      return;
    }
    send.mutate({
      id: account.id,
      data: {
        to: toList,
        cc: showCc ? parseAddresses(cc) : [],
        subject,
        bodyText: body,
        inReplyTo: initial?.inReplyTo ?? null,
        references: initial?.references ?? null,
      },
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full"
      data-testid="mail-composer"
    >
      <header className="flex items-center justify-between px-5 py-3 border-b border-border">
        <div>
          <p className="text-xs text-muted-foreground">Nouveau message</p>
          <p className="text-sm font-medium">De : {account.emailAddress}</p>
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        <div>
          <Label htmlFor="composer-to" className="text-xs">À</Label>
          <Input
            id="composer-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="destinataire@domaine.fr"
            data-testid="input-to"
          />
        </div>
        {showCc ? (
          <div>
            <Label htmlFor="composer-cc" className="text-xs">Cc</Label>
            <Input
              id="composer-cc"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="copie@domaine.fr"
            />
          </div>
        ) : (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0 h-auto"
            onClick={() => setShowCc(true)}
          >
            + Ajouter Cc
          </Button>
        )}
        <div>
          <Label htmlFor="composer-subject" className="text-xs">Objet</Label>
          <Input
            id="composer-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Objet"
            data-testid="input-subject"
          />
        </div>
        <div className="flex-1">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Écris ton message…"
            className="min-h-[260px] font-sans"
            data-testid="textarea-body"
          />
        </div>
      </div>

      <footer className="px-5 py-3 border-t border-border flex items-center gap-2">
        <Button
          type="submit"
          disabled={send.isPending}
          data-testid="button-send"
        >
          {send.isPending ? "Envoi…" : "Envoyer"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Annuler
        </Button>
      </footer>
    </form>
  );
}
